/**
 * buildJourneyShareUrl — Constructs the OG image URL for rewrite journey share cards
 *
 * Generates a URL to the generate-og-image edge function with type=rewrite-journey
 * and all required query parameters. The returned URL renders a 1200x630 PNG
 * showing a before/after score comparison card.
 *
 * Task: #1884 — Rewrite Journey Share Card
 * Agent: Diego <diego@liftping.net>
 */

import { getEdgeFunctionUrl } from '../../lib/api-url';

// ============================================================================
// Types
// ============================================================================

export interface JourneyShareParams {
  /** Original drama score before rewrites */
  originalScore: number;
  /** Revised drama score after rewrites */
  revisedScore: number;
  /** Original score category (safe, caution, high_drama, extreme_drama) */
  originalCategory: string;
  /** Revised score category */
  revisedCategory: string;
  /** Narrator quote for the card (will be URL-encoded) */
  narratorQuote: string;
  /** Number of accepted rewrites */
  acceptedCount: number;
  /** Score improvement delta (originalScore - revisedScore) */
  delta: number;
}

// ============================================================================
// URL builder
// ============================================================================

/**
 * Build the full URL for a rewrite journey OG image.
 *
 * @param params - Journey data for the share card
 * @returns Full URL string to the generate-og-image edge function
 *
 * @example
 * ```ts
 * const url = buildJourneyShareUrl({
 *   originalScore: 6.2,
 *   revisedScore: 2.1,
 *   originalCategory: 'caution',
 *   revisedCategory: 'safe',
 *   narratorQuote: 'From drama to zen in one edit session',
 *   acceptedCount: 4,
 *   delta: 4.1,
 * });
 * // => https://api-staging.dramadetector.com/generate-og-image?type=rewrite-journey&...
 * ```
 */
export function buildJourneyShareUrl(params: JourneyShareParams): string {
  const base = getEdgeFunctionUrl('generate-og-image');

  const searchParams = new URLSearchParams({
    type: 'rewrite-journey',
    originalScore: String(params.originalScore),
    revisedScore: String(params.revisedScore),
    originalCategory: params.originalCategory,
    revisedCategory: params.revisedCategory,
    narratorQuote: params.narratorQuote,
    acceptedCount: String(params.acceptedCount),
    delta: String(params.delta),
  });

  return `${base}?${searchParams.toString()}`;
}
